import { Box, IconButton, DialogTitle, Typography, Theme, useTheme, Stack, styled } from '@mui/material'
import React, { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ICustomerFormSchema, IFormSchemaReport } from '../generated-types/form-schema'
import { IMainComponent } from '../generated-types/main-component'
import { ShowDialogProps } from '../show-dialog'
import { getThemeColor } from 'src/utils'
import { MUIThemeColors } from 'src/mui-theme'
import { Flex } from 'src/styles/flexComponent'
import { Close } from '@mui/icons-material'
import { GenericDialogLayout } from 'src/components/GenericDialogLayout'
import { GridActionButton } from 'src/components/common/GridActionButton'
import { MovementChecklists } from 'src/components/checklists/MovementChecklists'
import { BulkMovementChecklists } from 'src/components/checklists/BulkMovementChecklists'
import { schemaHasRequiredFields } from 'src/components/checklists/checklist-utils'
import { useDialogCtx } from 'src/hooks/context-hooks'

interface CompleteChecklistsDialogProps extends ShowDialogProps {
  customerId: EntityId
  schemas: ICustomerFormSchema[]
  isLoadingSchemas?: boolean
  confirmText?: string
  mainComponentId?: EntityId
  isBulk?: boolean
  selectedEquipments?: IMainComponent[]
  returnAsMap?: boolean
}

export const CompleteChecklistsDialog: React.FC<CompleteChecklistsDialogProps> = ({
  pop,
  customerId,
  schemas,
  isLoadingSchemas,
  confirmText,
  mainComponentId,
  isBulk,
  selectedEquipments,
  returnAsMap,
}) => {
  const theme = useTheme()
  const { t } = useTranslation(['checklist', 'common'])
  const { showConfirmDialog } = useDialogCtx()
  const [reports, setReports] = useState<IFormSchemaReport[]>([])
  const [equipmentReportsMap, setEquipmentReportsMap] = useState<Map<number, IFormSchemaReport[]>>(new Map())
  const [isComplete, setIsComplete] = useState(false)

  const hasRequiredFields = useMemo(() => schemas.some(schemaHasRequiredFields), [schemas])

  const handleCancel = () => {
    showConfirmDialog(
      t('cancel-action-title', { ns: 'checklist' }),
      t('cancel-action-text', { ns: 'checklist' }),
      { acceptText: t('yes', { ns: 'common' }), cancelText: t('no', { ns: 'common' }) },
      closeConfirm => {
        closeConfirm()
        pop(null)
      },
    )
  }

  const handleConfirm = () => {
    const isBulkMode = !!(isBulk && selectedEquipments)
    const isIncomplete = isBulkMode ? !isComplete : !isComplete && hasRequiredFields

    if (isIncomplete) {
      showConfirmDialog(
        t(isBulkMode ? 'incomplete-equipment-checklists-title' : 'incomplete-checklists-title', { ns: 'checklist' }),
        t(isBulkMode ? 'incomplete-equipment-checklists-text' : 'incomplete-checklists-text', { ns: 'checklist' }),
        { acceptText: t('send-anyway', { ns: 'checklist' }), cancelText: t('back', { ns: 'common' }) },
        closeConfirm => {
          closeConfirm()
          pop(returnAsMap ? equipmentReportsMap : reports)
        },
      )
    } else {
      pop(returnAsMap ? equipmentReportsMap : reports)
    }
  }

  return (
    <GenericDialogLayout
      pop={handleCancel}
      removedCloseButton={true}
      actions={[
        <GridActionButton
          key="cancel"
          buttonText={t('cancel', { ns: 'common' })}
          onClick={handleCancel}
          shadowWeight={null}
          sx={{ border: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryMain)}` }}
          textColor={getThemeColor(theme, MUIThemeColors.secondaryMain)}
        />,
        <GridActionButton
          key="confirm"
          buttonText={confirmText ?? t('Send', { ns: 'common' })}
          onClick={handleConfirm}
          bgColor={getThemeColor(theme, MUIThemeColors.primaryMain)}
          textColor={getThemeColor(theme, MUIThemeColors.white)}
          shadowWeight={null}
          border={null}
        />,
      ]}
      renderCustomHeader={pop => (
        <HeaderContainer theme={theme}>
          <StyledDialogTitle>
            <HeaderStack>
              <HeaderTitle theme={theme}>{t('complete-checklists', { ns: 'checklist' })}</HeaderTitle>
              <Flex.Row alignItems="center" gap={1}>
                {pop && (
                  <CloseIconButton aria-label="close" onClick={pop} theme={theme}>
                    <Close />
                  </CloseIconButton>
                )}
              </Flex.Row>
            </HeaderStack>
          </StyledDialogTitle>
        </HeaderContainer>
      )}
    >
      {isBulk && selectedEquipments ? (
        <BulkMovementChecklists
          customerId={customerId}
          schemas={schemas}
          selectedEquipments={selectedEquipments}
          isLoading={isLoadingSchemas}
          onChange={(equipmentReports, complete) => {
            setIsComplete(complete)
            if (returnAsMap) {
              setEquipmentReportsMap(equipmentReports)
            } else {
              const allReports: IFormSchemaReport[] = []
              equipmentReports.forEach(r => allReports.push(...r))
              setReports(allReports)
            }
          }}
        />
      ) : (
        <MovementChecklists
          customerId={customerId}
          schemas={schemas}
          isLoading={isLoadingSchemas}
          onChange={(newReports, complete) => {
            setIsComplete(complete)
            setReports(newReports)
          }}
          mainComponentId={mainComponentId}
        />
      )}
    </GenericDialogLayout>
  )
}

const HeaderContainer = styled(Box)<{ theme: Theme }>(({ theme }) => ({
  backgroundColor: getThemeColor(theme, MUIThemeColors.white),
  borderBottom: `1px solid ${getThemeColor(theme, MUIThemeColors.secondaryLight)}`,
  height: '50px',
}))

const StyledDialogTitle = styled(DialogTitle)({
  padding: '12px 16px 0 16px',
})

const HeaderStack = styled(Stack)({
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  marginTop: '-5px',
})

const HeaderTitle = styled(Typography)<{ theme: Theme }>(({ theme }) => ({
  fontSize: theme.typography.subtitle1.fontSize,
  fontWeight: 700,
  color: getThemeColor(theme, MUIThemeColors.primaryDark),
}))

const CloseIconButton = styled(IconButton)<{ theme: Theme }>(({ theme }) => ({
  '& svg': {
    color: getThemeColor(theme, MUIThemeColors.secondaryMain),
    fontSize: '18px',
  },
}))
